import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { lighten, makeStyles } from '@material-ui/core/styles';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TableSortLabel, Toolbar, Typography, Paper, Checkbox,
} from '@material-ui/core';
import Images from '../basics/Images';

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

const headCells = [
  { id: 'id', numeric: true, disablePadding: true, label: 'ID' },
  { id: 'images', numeric: false, disablePadding: false, label: 'Cover Image' },
  { id: 'title', numeric: false, disablePadding: false, label: 'Title' },
  { id: 'brand', numeric: false, disablePadding: false, label: 'Brand' },
  { id: 'categories', numeric: false, disablePadding: false, label: 'Categories' },
  { id: 'type', numeric: false, disablePadding: false, label: 'Type' },
  { id: 'status', numeric: false, disablePadding: false, label: 'Status' },
];

function EnhancedTableHead(props) {
  const { classes, order, orderBy, onRequestSort } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        <TableCell padding="checkbox" />
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            padding={headCell.disablePadding ? 'none' : 'default'}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <span className={classes.visuallyHidden}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </span>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  classes:       PropTypes.object.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  order:         PropTypes.oneOf(['asc', 'desc']).isRequired,
  orderBy:       PropTypes.string.isRequired,
};

const useToolbarStyles = makeStyles((theme) => ({
  root: {
    paddingLeft:  theme.spacing(2),
    paddingRight: theme.spacing(1),
  },
  highlight:
    theme.palette.type === 'light'
      ? {
        color:           theme.palette.secondary.main,
        backgroundColor: lighten(theme.palette.secondary.light, 0.85),
      }
      : {
        color:           theme.palette.text.primary,
        backgroundColor: theme.palette.secondary.dark,
      },
  title: {
    flex: '1 1 100%',
  },
}));

const EnhancedTableToolbar = (props) => {
  const classes = useToolbarStyles();
  return (
    <Toolbar className={clsx(classes.root)}>
      <Typography className={classes.title} variant="h6" id="tableTitle" component="div">
        Posts
      </Typography>
    </Toolbar>
  );
};

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
  },
  paper: {
    width:        '100%',
    marginBottom: theme.spacing(2),
  },
  table: {
    minWidth: 750,
  },
  visuallyHidden: {
    border:   0,
    clip:     'rect(0 0 0 0)',
    height:   1,
    margin:   -1,
    overflow: 'hidden',
    padding:  0,
    position: 'absolute',
    top:      20,
    width:    1,
  },
}));

const PostList = ({ rows, pageNo, pageSize, posts, postId, totalCount, onChangedPostId, onChangedTable }) => {
  const classes = useStyles();
  const [order, setOrder] = React.useState('asc');
  const [orderBy, setOrderBy] = React.useState('calories');
  const [page, setPage] = React.useState(pageNo);
  const [rowsPerPage, setRowsPerPage] = React.useState(pageSize);
  const [loadedPosts, setLoadedPosts] = React.useState([]);

  useEffect(() => {
    const loadedPosts = posts.map((post) => {
      if (post.id === postId) {
        return { ...post, selected: -1 };
      } else {
        return { ...post, selected: 0 };
      }
    });
    setLoadedPosts(loadedPosts);
  }, [posts]);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  useEffect(() => {
    setPage(pageNo);
  }, [pageNo]);

  const handleClick = (event, id) => {
    const selectedPost = loadedPosts.find((post) => post.id === id);
    if (selectedPost.selected === -1) {
      var updatedPosts = (loadedPosts.map((post) => (post.id === id ? { ...post, selected: 0 } : post)))
        .map((post) => (post.id !== id ? { ...post, selected: 0 } : post));
      setLoadedPosts(updatedPosts);
      onChangedPostId('');
    } else {
      var updatedPosts = (loadedPosts.map((post) => (post.id === id ? { ...post, selected: -1 } : post)))
        .map((post) => (post.id !== id ? { ...post, selected: 0 } : post));
      setLoadedPosts(updatedPosts);
      onChangedPostId(id);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    onChangedTable(newPage, rowsPerPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    onChangedTable(0, parseInt(event.target.value, 10));
  };

  const renderCategories = (array) => {
    const categories = array.map((category) => category.name);
    return categories.toString().replace(/,/g, ', ');
  };

  return (
    <div className={classes.root}>
      <Paper className={classes.paper}>
        <EnhancedTableToolbar />
        <TableContainer>
          <Table
            className={classes.table}
            aria-labelledby="tableTitle"
            size="medium"
            aria-label="enhanced table"
          >
            <EnhancedTableHead
              classes={classes}
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
            />
            <TableBody>
              {stableSort(loadedPosts, getComparator(order, orderBy))
                .map((row, index) => {
                  const labelId = `enhanced-table-checkbox-${index}`;
                  return (
                    <TableRow
                      hover
                      onClick={(event) => handleClick(event, row.id)}
                      role="checkbox"
                      aria-checked={row.selected}
                      tabIndex={-1}
                      key={row.id}
                      selected={row.selected === -1}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={row.selected === -1}
                          inputProps={{ 'aria-labelledby': labelId }}
                        />
                      </TableCell>
                      <TableCell component="th" id={labelId} scope="row" padding="none">
                        {row.id}
                      </TableCell>
                      <TableCell>
                        {row.images.length > 0
                          ? (
                            <img
                              style={{
                                borderRadius: '5px',
                                minWidth:     '50px',
                                maxWidth:     '50px',
                                minHeight:    '50px',
                                maxHeight:    '50px',
                              }}
                              src={row.images[0].display}
                              alt=""
                            />
                          )
                          : (
                            <img
                              style={{
                                borderRadius: '5px',
                                minWidth:     '50px',
                                maxWidth:     '50px',
                                minHeight:    '50px',
                                maxHeight:    '50px',
                              }}
                              src={Images.Local.Placeholder}
                              alt=""
                            />
                          )}
                      </TableCell>
                      <TableCell>{row.title}</TableCell>
                      <TableCell>{row.brand}</TableCell>
                      <TableCell>{renderCategories(row.categories)}</TableCell>
                      <TableCell>{row.type}</TableCell>
                      <TableCell>{row.status}</TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={rows}
          component="div"
          count={Number(totalCount)}
          rowsPerPage={rowsPerPage}
          page={page}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
        />
      </Paper>
    </div>
  );
};

export default PostList;
